import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";

export function ModulesIndex() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/modules").then((r) => setItems(r.data || [])); }, []);

  return (
    <PublicLayout>
      <section className="border-b border-[var(--ink)] bg-[var(--parchment)] py-16 sm:py-20" data-testid="modules-index">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="eyebrow">Service Architecture</div>
          <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-4 leading-[0.95] tracking-tight">
            Fifteen Intelligence Modules.<br/>
            <span className="italic font-medium text-[var(--oxblood)]">One advisory practice.</span>
          </h1>
          <p className="mt-6 font-display italic text-2xl text-[var(--ink-soft)] max-w-3xl">
            Every assignment maps to one or more of fifteen disciplines.
            Each is a forensic-grade methodology designed for a single strategic question.
          </p>
        </div>
      </section>

      <section className="border-b border-[var(--ink)] bg-[var(--parchment)] pb-16 sm:pb-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ink)] border border-[var(--ink)]">
            {items.map((m) => (
              <Link key={m.slug} to={`/modules/${m.slug}`} className="bg-[var(--parchment)] p-7 group hover:bg-white transition-colors" data-testid={`module-list-${m.slug}`}>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--ink-soft)]">No. {String(m.order).padStart(2, "0")}</div>
                <h3 className="font-display font-bold text-2xl mt-3 text-[var(--ink)] group-hover:text-[var(--oxblood)] transition-colors leading-tight">{m.name}</h3>
                <p className="mt-2 text-sm text-[var(--oxblood)] font-display italic">{m.tagline}</p>
                <p className="mt-4 text-sm text-[var(--ink-soft)] leading-relaxed">{m.strategic_question}</p>
                <div className="mt-6 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--ink)] flex items-center gap-1">
                  Methodology <ArrowRight size={12} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

export function ModuleDetail() {
  const { slug } = useParams();
  const [m, setM] = useState(null);
  useEffect(() => { api.get(`/modules/${slug}`).then((r) => setM(r.data)).catch(() => {}); }, [slug]);

  if (!m) return <PublicLayout><div className="max-w-[1400px] mx-auto px-6 py-20 font-mono text-sm">Loading…</div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="border-b border-[var(--ink)] bg-[var(--parchment)] py-16 sm:py-20" data-testid="module-page">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <Link to="/modules" className="eyebrow hover:text-[var(--ink)]">← All modules</Link>
          <div className="mt-4 text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--ink-soft)]">Intelligence Module No. {String(m.order).padStart(2, "0")}</div>
          <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-3 leading-[0.95] tracking-tight" data-testid="module-title">{m.name}</h1>
          <p className="mt-6 font-display italic text-2xl text-[var(--ink-soft)] max-w-3xl">{m.tagline}</p>

          <div className="mt-12 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="bg-white border border-[var(--border-soft)] p-7 sm:p-9">
                <div className="eyebrow">Strategic Question</div>
                <p className="mt-4 font-display text-2xl sm:text-3xl leading-snug">{m.strategic_question}</p>
              </div>

              <div className="mt-10">
                <div className="eyebrow">Methodology</div>
                <p className="mt-4 text-[17px] leading-relaxed text-[var(--ink)]">{m.methodology}</p>
              </div>

              <div className="mt-10 grid sm:grid-cols-2 gap-8">
                <div>
                  <div className="eyebrow">Output Architecture</div>
                  <ul className="mt-4 space-y-2">
                    {(m.output_architecture || []).map((o, i) => (
                      <li key={i} className="flex gap-3 text-[15px] leading-relaxed">
                        <CheckCircle2 size={16} className="text-[var(--viridian)] flex-shrink-0 mt-1" />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="eyebrow">Decision Outcomes</div>
                  <ul className="mt-4 space-y-2">
                    {(m.decision_outcomes || []).map((o, i) => (
                      <li key={i} className="flex gap-3 text-[15px] leading-relaxed">
                        <span className="text-[var(--oxblood)] font-mono mt-1">→</span>
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {m.use_cases?.length > 0 && (
                <div className="mt-10">
                  <div className="eyebrow">Use Cases</div>
                  <ul className="mt-4 space-y-4">
                    {m.use_cases.map((u, i) => (
                      <li key={i} className="border-l-2 border-[var(--oxblood)] pl-5 font-display italic text-lg leading-snug">"{u}"</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="bg-[var(--ink)] text-[var(--parchment)] p-7 sticky top-28">
                <div className="eyebrow text-[var(--brass)]">Engage</div>
                <h3 className="font-display font-black text-2xl mt-3 leading-tight">Discuss this module with our practice lead.</h3>
                <ul className="mt-5 space-y-2 text-sm text-[var(--parchment)]/80">
                  {(m.deliverables || []).map((d, i) => (
                    <li key={i} className="pl-5 relative"><span className="absolute left-0 text-[var(--brass)]">—</span>{d}</li>
                  ))}
                </ul>
                <Link to="/strategy-session" className="btn-primary w-full justify-center mt-7" data-testid="module-cta">
                  Schedule a Strategy Session <ArrowRight size={14} />
                </Link>
                <Link to="/luminous" className="block text-center mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--brass)] hover:underline">
                  Or try Luminous Intelligence →
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
